import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  OnInit,
  Injector,
  ChangeDetectorRef,
  OnChanges,
  inject,
  SimpleChanges,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  Validator,
  AbstractControl,
  ValidationErrors,
  NgControl,
  ReactiveFormsModule,
  FormsModule,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ColorPickerModule } from '@progress/kendo-angular-inputs';
import { IconsModule } from '@progress/kendo-angular-icons';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { xIcon, paletteIcon } from '@progress/kendo-svg-icons';

export interface ColorpickerConfig {
  label?: string;
  placeholder?: string;
  format?: 'hex' | 'rgba' | 'name';
  disabled?: boolean;
  readonly?: boolean;
  required?: boolean;
  showErrors?: boolean;
  hint?: string;
  size?: 'small' | 'medium' | 'large';
  fillMode?: 'solid' | 'outline' | 'flat';
  clearButton?: boolean;
  tabIndex?: number;
  value?: string;
  view?: 'simple' | 'gradient' | 'palette';
  palette?: string[] | 'basic' | 'websafe' | 'office';
  paletteColumns?: number;
  opacity?: boolean;
  showInput?: boolean;
  allowManualInput?: boolean;
  previewSize?: number;
  quickColors?: string[];
  contrastTool?: boolean;
}

export interface ColorValidationConfig {
  required?: boolean;
  customValidators?: Array<(value: string | null) => ValidationErrors | null>;
  errorMessages?: Record<string, string>;
  allowedColors?: string[];
  disallowedColors?: string[];
  minBrightness?: number;
  maxBrightness?: number;
  minSaturation?: number;
  maxSaturation?: number;
  contrastRatio?: {
    backgroundColor?: string;
    minRatio?: number;
  };
}

interface TileSize {
  width: number;
  height: number;
}

@Component({
  selector: 'app-colorpicker',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    ColorPickerModule,
    IconsModule,
    ButtonsModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ColorpickerComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => ColorpickerComponent),
      multi: true,
    },
  ],
  templateUrl: './colorpicker.component.html',
  styleUrls: ['./colorpicker.component.scss'],
})
export class ColorpickerComponent implements ControlValueAccessor, Validator, OnInit, OnChanges {
  @Input() config: ColorpickerConfig = {};
  @Input() validation: ColorValidationConfig = {};

  @Output() valueChange = new EventEmitter<string | null>();
  @Output() onFocus = new EventEmitter<FocusEvent>();
  @Output() onBlur = new EventEmitter<FocusEvent>();
  @Output() onOpen = new EventEmitter<void>();
  @Output() onClose = new EventEmitter<void>();
  @Output() onClear = new EventEmitter<void>();
  @Output() onQuickColorSelect = new EventEmitter<string>();

  paletteIconSvg = paletteIcon;
  xIconSvg = xIcon;

  value: string | null = null;
  displayValue: string = '';
  touched = false;
  errors: ValidationErrors | null = null;
  inputId = `colorpicker-${crypto.randomUUID?.() ?? Math.random().toString(36).slice(2, 11)}`;

  paletteColors: string[] = [];
  tileSize: TileSize = { width: 24, height: 24 };

  private onTouchedCallback: () => void = () => {};
  private onChangeCallback: (_: any) => void = () => {};
  private readonly cdr = inject(ChangeDetectorRef);

  public ngControl: NgControl | null = null;

  constructor(private readonly injector: Injector) {}

  ngOnInit() {
    this.ngControl = this.injector.get(NgControl, null);
    this.initializeDefaults();
    this.setupPalette();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['config']) {
      if (this.config.value) {
        this.value = this.normalizeColor(this.config.value);
        this.updateDisplayValue();
      }
      this.setupPalette();
    }
  }

  get colorValue(): string {
    return this.value || '#000000';
  }

  set colorValue(val: string) {
    this.value = val;
  }

  get kendoFormat(): 'hex' | 'rgba' | 'name' {
    const format = this.config.format;
    if (format === 'rgba' || format === 'name') {
      return format;
    }
    return 'hex';
  }

  private initializeDefaults() {
    this.config = {
      format: 'hex',
      size: 'medium',
      fillMode: 'outline',
      showErrors: true,
      clearButton: false,
      disabled: false,
      readonly: false,
      required: false,
      view: 'simple',
      paletteColumns: 10,
      opacity: false,
      showInput: true,
      allowManualInput: true,
      previewSize: 30,
      contrastTool: false,
      ...this.config,
    };

    this.validation = {
      required: this.config.required,
      errorMessages: {
        required: 'This field is required',
        invalidColor: 'Please enter a valid color',
        notAllowed: 'This color is not allowed',
        disallowed: 'This color is not available',
        brightness: 'Color brightness is out of range',
        saturation: 'Color saturation is out of range',
        contrast: 'Color does not meet contrast requirements',
        ...this.validation.errorMessages,
      },
      ...this.validation,
    };
  }

  private setupPalette() {
    if (typeof this.config.palette === 'string') {
      switch (this.config.palette) {
        case 'basic':
          this.paletteColors = [
            '#000000',
            '#7f7f7f',
            '#880015',
            '#ed1c24',
            '#ff7f27',
            '#fff200',
            '#22b14c',
            '#00a2e8',
            '#3f48cc',
            '#a349a4',
            '#ffffff',
            '#c3c3c3',
            '#b97a57',
            '#ffaec9',
            '#ffc90e',
            '#efe4b0',
            '#b5e61d',
            '#99d9ea',
            '#7092be',
            '#c8bfe7',
          ];
          break;
        case 'websafe':
          this.paletteColors = this.generateWebsafePalette();
          break;
        case 'office':
          this.paletteColors = [
            '#ffffff',
            '#000000',
            '#eeece1',
            '#1f497d',
            '#4f81bd',
            '#c0504d',
            '#9bbb59',
            '#8064a2',
            '#4bacc6',
            '#f79646',
            '#f2f2f2',
            '#7f7f7f',
            '#ddd9c3',
            '#c6d9f0',
            '#dbe5f1',
            '#f2dcdb',
            '#ebf1dd',
            '#e5e0ec',
            '#dbeef3',
            '#fdeada',
            '#d8d8d8',
            '#595959',
            '#c4bd97',
            '#8db3e2',
            '#b8cce4',
            '#e5b9b7',
            '#d7e3bc',
            '#ccc1d9',
            '#b7dde8',
            '#fbd5b5',
            '#bfbfbf',
            '#3f3f3f',
            '#938953',
            '#548dd4',
            '#95b3d7',
            '#d99694',
            '#c3d69b',
            '#b2a2c7',
            '#92cddc',
            '#fac08f',
            '#a5a5a5',
            '#262626',
            '#494429',
            '#17365d',
            '#366092',
            '#953734',
            '#76923c',
            '#5f497a',
            '#31859b',
            '#e36c09',
            '#7f7f7f',
            '#0c0c0c',
            '#1d1b10',
            '#0f243e',
            '#244061',
            '#632423',
            '#4f6128',
            '#3f3151',
            '#205867',
            '#974806',
          ];
          break;
        default:
          this.paletteColors = this.config.palette as string[];
      }
    } else if (Array.isArray(this.config.palette)) {
      this.paletteColors = this.config.palette;
    } else {
      this.paletteColors = [
        '#000000',
        '#7f7f7f',
        '#880015',
        '#ed1c24',
        '#ff7f27',
        '#fff200',
        '#22b14c',
        '#00a2e8',
        '#3f48cc',
        '#a349a4',
        '#ffffff',
        '#c3c3c3',
        '#b97a57',
        '#ffaec9',
        '#ffc90e',
      ];
    }

    const tileWidth = this.config.size === 'small' ? 20 : this.config.size === 'large' ? 32 : 24;
    this.tileSize = { width: tileWidth, height: tileWidth };
  }

  private generateWebsafePalette(): string[] {
    const values = ['00', '33', '66', '99', 'CC', 'FF'];
    const palette: string[] = [];
    for (const r of values) {
      for (const g of values) {
        for (const b of values) {
          palette.push(`#${r}${g}${b}`);
        }
      }
    }
    return palette;
  }

  private normalizeColor(color: string | null): string | null {
    if (!color) return null;

    color = color.trim();

    if (color.startsWith('#')) {
      if (color.length === 4) {
        const r = color[1];
        const g = color[2];
        const b = color[3];
        return `#${r}${r}${g}${g}${b}${b}`;
      }
      return color.toUpperCase();
    }

    if (color.startsWith('rgb')) {
      return this.rgbToHex(color);
    }

    return color;
  }

  private rgbToHex(rgb: string): string {
    const result = rgb.match(/\d+/g);
    if (result && result.length >= 3) {
      const r = parseInt(result[0]).toString(16).padStart(2, '0');
      const g = parseInt(result[1]).toString(16).padStart(2, '0');
      const b = parseInt(result[2]).toString(16).padStart(2, '0');
      return `#${r}${g}${b}`.toUpperCase();
    }
    return '#000000';
  }

  private hexToRgb(hex: string): { r: number; g: number; b: number } | null {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16),
        }
      : null;
  }

  private calculateBrightness(color: string): number {
    const rgb = this.hexToRgb(color);
    if (!rgb) return 0;
    return (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
  }

  private calculateSaturation(color: string): number {
    const rgb = this.hexToRgb(color);
    if (!rgb) return 0;

    const r = rgb.r / 255;
    const g = rgb.g / 255;
    const b = rgb.b / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const delta = max - min;

    if (max === 0) return 0;
    return (delta / max) * 100;
  }

  private calculateContrastRatio(color1: string, color2: string): number {
    const lum1 = this.calculateLuminance(color1);
    const lum2 = this.calculateLuminance(color2);
    const brightest = Math.max(lum1, lum2);
    const darkest = Math.min(lum1, lum2);
    return (brightest + 0.05) / (darkest + 0.05);
  }

  private calculateLuminance(color: string): number {
    const rgb = this.hexToRgb(color);
    if (!rgb) return 0;

    const a = [rgb.r, rgb.g, rgb.b].map((v) => {
      v = v / 255;
      return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    });

    return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
  }

  private updateDisplayValue() {
    if (!this.value) {
      this.displayValue = '';
      return;
    }

    switch (this.config.format) {
      case 'rgba':
        const rgb = this.hexToRgb(this.value);
        if (rgb) {
          this.displayValue = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 1)`;
        }
        break;
      case 'hex':
      default:
        this.displayValue = this.value;
        break;
    }
  }

  writeValue(value: any): void {
    if (typeof value === 'string' && value) {
      this.value = this.normalizeColor(value);
    } else {
      this.value = null;
    }
    this.updateDisplayValue();
    this.cdr.markForCheck();
  }

  registerOnChange(fn: any): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedCallback = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.config.disabled = isDisabled;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    const errors: ValidationErrors = {};

    if (!value) {
      if (this.config.required || this.validation?.required) {
        errors['required'] = true;
      }
      this.errors = Object.keys(errors).length ? errors : null;
      return this.errors;
    }

    const color = this.normalizeColor(value);
    if (!color || !this.isValidColor(color)) {
      errors['invalidColor'] = true;
      this.errors = errors;
      return errors;
    }

    this.checkAllowedDisallowedColors(color, errors);
    this.checkBrightnessRange(color, errors);
    this.checkSaturationRange(color, errors);
    this.checkContrastRatio(color, errors);
    this.applyCustomValidators(color, errors);

    this.errors = Object.keys(errors).length ? errors : null;
    return this.errors;
  }

  private isValidColor(color: string): boolean {
    return (
      /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color) ||
      /^rgb\((\d{1,3}),\s*(\d{1,3}),\s*(\d{1,3})\)$/.test(color) ||
      /^rgba\((\d{1,3}),\s*(\d{1,3}),\s*(\d{1,3}),\s*([\d.]+)\)$/.test(color)
    );
  }

  private checkAllowedDisallowedColors(color: string, errors: ValidationErrors) {
    if (!this.validation) {
      return;
    }
    if (this.validation.allowedColors && this.validation.allowedColors.length > 0) {
      const normalized = this.validation.allowedColors.map((c) => this.normalizeColor(c));
      if (!normalized.includes(color)) {
        errors['notAllowed'] = true;
      }
    }

    if (this.validation.disallowedColors && this.validation.disallowedColors.length > 0) {
      const normalized = this.validation.disallowedColors.map((c) => this.normalizeColor(c));
      if (normalized.includes(color)) {
        errors['disallowed'] = true;
      }
    }
  }

  private checkBrightnessRange(color: string, errors: ValidationErrors) {
    if (!this.validation) {
      return;
    }
    const brightness = this.calculateBrightness(color);

    if (this.validation.minBrightness !== undefined && brightness < this.validation.minBrightness) {
      errors['brightness'] = true;
    }

    if (this.validation.maxBrightness !== undefined && brightness > this.validation.maxBrightness) {
      errors['brightness'] = true;
    }
  }

  private checkSaturationRange(color: string, errors: ValidationErrors) {
    if (!this.validation) {
      return;
    }
    const saturation = this.calculateSaturation(color);

    if (this.validation.minSaturation !== undefined && saturation < this.validation.minSaturation) {
      errors['saturation'] = true;
    }

    if (this.validation.maxSaturation !== undefined && saturation > this.validation.maxSaturation) {
      errors['saturation'] = true;
    }
  }

  private checkContrastRatio(color: string, errors: ValidationErrors) {
    if (!this.validation) {
      return;
    }
    if (!this.validation.contrastRatio) return;

    const backgroundColor = this.validation.contrastRatio.backgroundColor || '#FFFFFF';
    const minRatio = this.validation.contrastRatio.minRatio || 4.5;

    const ratio = this.calculateContrastRatio(color, backgroundColor);
    if (ratio < minRatio) {
      errors['contrast'] = true;
    }
  }

  private applyCustomValidators(color: string, errors: ValidationErrors) {
    if (!this.validation) {
      return;
    }
    if (!this.validation.customValidators) return;
    for (const validator of this.validation.customValidators) {
      const customErr = validator(color);
      if (customErr) Object.assign(errors, customErr);
    }
  }

  get showErrorState(): boolean {
    if (!this.config.showErrors) return false;
    const hasErrors = !!(this.errors ?? this.ngControl?.control?.errors);
    if (!hasErrors) return false;

    const isTouched =
      this.touched ?? this.ngControl?.control?.touched ?? this.ngControl?.control?.dirty;
    return !!isTouched;
  }

  get currentErrorMessage(): string {
    if (!this.showErrorState) return '';
    const errors = this.ngControl?.control?.errors ?? this.errors;
    if (!errors) return '';
    const firstKey = Object.keys(errors)[0];
    return this.validation.errorMessages?.[firstKey] ?? 'Invalid color';
  }

  get hasValue(): boolean {
    return this.value !== null && this.value !== undefined && this.value !== '';
  }

  get showClearButton(): boolean {
    return !!(this.config.clearButton && this.hasValue && !this.config.disabled);
  }

  handleValueChange(value: string) {
    const normalizedValue = value ? this.normalizeColor(value) : null;
    this.value = normalizedValue;
    this.updateDisplayValue();
    this.onChangeCallback(this.value);
    this.valueChange.emit(this.value);

    if (this.ngControl?.control) {
      this.ngControl.control.updateValueAndValidity();
    }
    this.cdr.markForCheck();
  }

  handleManualInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    if (this.isValidColor(value)) {
      this.value = this.normalizeColor(value);
      this.onChangeCallback(this.value);
      this.valueChange.emit(this.value);

      if (this.ngControl?.control) {
        this.ngControl.control.updateValueAndValidity();
      }
    }

    this.displayValue = value;
    this.cdr.markForCheck();
  }

  handleInputBlur() {
    this.touched = true;
    this.onTouchedCallback();

    if (this.value && this.isValidColor(this.displayValue)) {
      this.value = this.normalizeColor(this.displayValue);
      this.onChangeCallback(this.value);
    } else {
      this.updateDisplayValue();
    }

    this.cdr.markForCheck();
  }

  handleBlur() {
    this.touched = true;
    this.onTouchedCallback();
    this.onBlur.emit();
  }

  handleFocus(event: FocusEvent) {
    this.onFocus.emit(event);
  }

  handleOpen() {
    this.onOpen.emit();
  }

  handleClose() {
    this.onClose.emit();
  }

  onFileSelectClick() {
    // Placeholder for opening color picker programmatically if needed
  }

  selectQuickColor(color: string) {
    this.value = this.normalizeColor(color);
    this.updateDisplayValue();
    this.touched = true;
    this.onChangeCallback(this.value);
    this.onTouchedCallback();
    this.valueChange.emit(this.value);
    this.onQuickColorSelect.emit(color);

    if (this.ngControl?.control) {
      this.ngControl.control.updateValueAndValidity();
    }
    this.cdr.markForCheck();
  }

  clearColor() {
    this.value = null;
    this.displayValue = '';
    this.touched = true;
    this.onChangeCallback(this.value);
    this.onTouchedCallback();
    this.valueChange.emit(this.value);
    this.onClear.emit();

    if (this.ngControl?.control) {
      this.ngControl.control.updateValueAndValidity();
    }
    this.cdr.markForCheck();
  }
}
